import React, { useEffect, useState } from 'react';

import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Alert, Button, Skeleton } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { NewLogo } from 'components/base/SVG';
import NewLogoSmall from 'components/base/SVG/NewLogoSmall';
import {
  LinkedInPreview,
  formatHashtags,
} from 'routes/Advocate/MyPost/components/ListSocialPosts/components/List/ShareList/exports';
import { getPublicAmplifiedTemplateDetail } from 'services/advocate';
import { AmplifiedPostDetail } from 'types/AmplifiedTemplate';

const data = [
  {
    title: 'Track your impact',
    description: 'Real-time analytics on every post you share',
  },
  {
    title: 'Never run out of ideas',
    description: 'Fresh, on-brand content',
  },
  {
    title: 'Post quickly',
    description: 'Publish seamlessly without the overhead',
  },
];

export default function AdvocacyShare() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId, templateId } = useParams();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const viaMagicLink = searchParams.get('via-magic-link');

  const handleNavigateToSignUp = () => {
    navigate(
      `/advocate/register/${companyId}/${token}/${templateId}?via-magic-link=${viaMagicLink}`,
      { state: { advocacySharePost: true } },
    );
  };

  const handleNavigateToSignIn = () => {
    navigate(
      `/advocate/login?shareAdvocacyPostId=${templateId}&token=${token}&companyId=${companyId}`,
      { state: { advocacySharePost: true } },
    );
  };

  const [detail, setDetail] = useState<AmplifiedPostDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!templateId) return;
    setIsLoading(true);
    setErrorMessage(null);
    setDetail(null);
    getPublicAmplifiedTemplateDetail(templateId)
      .then((res: any) => {
        setDetail(res?.data?.data?.detail ?? null);
      })
      .catch((err: any) => {
        console.log('Error in getPublicAmplifiedTemplateDetail:', err);
        setErrorMessage(t('FailedToLoadPostDetails'));
      })
      .finally(() => setIsLoading(false));
  }, [templateId]);

  const firstCopy = detail?.social_post_copies?.[0];
  const previewContent = firstCopy?.content ?? '';
  const previewCta = detail?.template_cta ?? '';
  const previewHashtags = formatHashtags(detail?.social_post_hashtags);
  const allAttachments = (detail?.social_post_attachments ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);

  const renderPreview = () => {
    if (isLoading) {
      return (
        <div className="rounded-2xl border border-[#f0ecee] bg-white p-[15px]">
          <Skeleton active avatar paragraph={{ rows: 6 }} />
        </div>
      );
    }
    if (errorMessage) {
      return (
        <Alert type="error" showIcon message={errorMessage} role="alert" />
      );
    }
    return (
      <LinkedInPreview
        previewContent={previewContent}
        previewCta={previewCta}
        previewHashtags={previewHashtags}
        attachments={allAttachments}
        showActions={false}
        containerClassName="rounded-2xl border border-[#f0ecee] bg-white p-[15px]"
      />
    );
  };

  return (
    <div className="min-h-screen bg-[#f8f7f8]">
      <header className="border-b border-[#f0ecee] bg-white px-8 py-3">
        <Link to="/">
          <NewLogo width={158} height={38} />
        </Link>
      </header>

      <div className="mx-auto max-w-[1078px] p-4 md:px-8 md:py-10">
        <div className="rounded-2xl border border-[#f0ecee] bg-white px-6 py-6 md:px-[39px] md:py-[39px]">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="shrink-0 pt-1">
                <NewLogoSmall width={54} height={68} />
              </div>
              <div>
                <h2 className="text-[24px] leading-[32px] font-bold text-[#070550]">
                  {t('AdvocacyShare.Title')}
                </h2>
                <p className="mt-3 max-w-[560px] text-[16px] leading-[21px] font-medium text-[#070550]">
                  {t('AdvocacyShare.Guide')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 self-start md:self-auto">
              <Button
                type="primary"
                className="!h-10 !rounded-xl !border-none !bg-[#91dbb6] !px-4 !text-[14px] !font-medium !text-[#070550] hover:!bg-[#7fd1a8]"
                onClick={handleNavigateToSignUp}
              >
                {t('AdvocacyShare.SignUpShare')}
              </Button>
              <Button
                type="default"
                className="!h-10 !rounded-xl !border-[#070550] !px-4 !text-[14px] !font-medium !text-[#070550] hover:!border-[#070550] hover:!text-[#070550]"
                onClick={handleNavigateToSignIn}
              >
                {t('AdvocacyShare.SignIn')}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col items-start gap-4 lg:flex-row lg:items-start lg:gap-5">
          <div className="w-full min-w-0 lg:flex-1">
            {renderPreview()}
          </div>
          <div className="w-full lg:w-[468px] lg:shrink-0">
            <div className="rounded-2xl border border-[#f0ecee] bg-white px-6 py-8 md:px-[39px] md:py-[39px]">
              <h3 className="text-[18px] leading-[24px] font-bold text-[#070550]">
                {t('AdvocacyShare.Why')}
              </h3>
              <div className="mt-7 space-y-6">
                {data.map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <CheckCircleOutlined className="mt-[1px] text-[18px] text-[#91dbb6]" />
                    <div>
                      <p className="text-[14px] leading-[21px] font-bold text-[#070550]">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-[14px] leading-[21px] text-[#070550]">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
